import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Alert, FundId, ShareClassCode } from '@types';
import { Logger } from '@utils/logger';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SegmentService, StorageService } from '@services';
import {
  DialogService,
  SimpleModalComponent,
  SimpleModalConfig,
} from '@frk/eds-components';

const logger = Logger.getLogger('FundHeaderAlertsComponent');

@Component({
  selector: 'ft-fund-header-alerts',
  templateUrl: './fund-header-alerts.component.html',
  styleUrls: ['./fund-header-alerts.component.scss'],
})
export class FundHeaderAlertsComponent implements OnDestroy, OnInit {
  /** Fund Id */
  @Input() fundId: FundId;

  /** Share class code */
  @Input() shareClassCode: ShareClassCode;

  public alerts: Alert[] = [];
  public showAlertPopup = false;
  public showFundAccordion: boolean;
  public alertConfig: SimpleModalConfig;
  public alertWithPopup: Alert;
  private unsubscribe$: Subject<void> = new Subject<void>();

  @ViewChild('modal', { static: false }) modal: SimpleModalComponent;

  constructor(
    //
    // responsiveService: ResponsiveService,
    private storageService: StorageService,
    private dialogService: DialogService,
    private caveatService: CaveatService,
    private segmentService: SegmentService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (this.fundId) {
      const alert$ = this.caveatService.getFundAlerts$(
        this.fundId,
        this.shareClassCode
      );
      const segment$ = this.segmentService?.getCurrentSegmentId$();

      // use combineLatest because alert always not to emit a new value as we change the segments.
      // https://www.learnrxjs.io/learn-rxjs/operators/combination/combinelatest

      combineLatest([alert$, segment$])
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(([alerts, segmentId]) => {
          this.showFundAccordion = false;
          alerts.forEach((alert) => {
            // By default global caveats have no segments set.
            // If no segment is set, the fund alert should be displayed for all actors:DDCE-189
            if (!alert.segments?.length) {
              this.showFundAccordion = true;
            } else {
              alert.segments?.some((segment) => {
                if (segment === segmentId) {
                  this.showFundAccordion = true;
                }
              });
            }
          });
          this.alerts = alerts;
          this.checkForAlertPopup();
        });
    }
  }

  private checkForAlertPopup() {
    if (this.alerts.length > 0) {
      this.alertWithPopup = this.alerts.find(
        // just show the first popup we find
        (alert: Alert) =>
          alert.popupCloseLabel &&
          alert.popupConfirmLabel &&
          alert.popupContent &&
          alert.popupTitle
      );
      if (this.alertWithPopup) {
        this.storageService
          .retrieve<string>(this.getAlertStorageKey(this.alertWithPopup), true)
          .then((hasConfirmed) => {
            if (!hasConfirmed) {
              this.alertConfig = {
                modalId: 'alertPopup',
                themeTitle: 'dark',
                closeBtnLabel: this.alertWithPopup?.popupCloseLabel,
                title: this.alertWithPopup?.popupTitle,
              };
              this.dialogService
                .getDialogClosedObs()
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((dialog: SimpleModalConfig) => {
                  // check this is our dialog
                  if (dialog?.modalId === 'alertPopup') {
                    logger.debug('dialog closed', dialog);
                    // currently only get message when confirm button is hit so just assume confirmed.
                    this.storageService.store<boolean>(
                      this.getAlertStorageKey(this.alertWithPopup),
                      true,
                      true
                    );
                  }
                });
              this.showAlertPopup = true;
              this.cdr.detectChanges();
            }
          });
      }
    }
  }

  private getAlertStorageKey(alertWithPopup: Alert): string {
    const fsc = `${this.fundId}-${this.shareClassCode}`;
    if (alertWithPopup.fscl?.includes(fsc)) {
      return `alert_${fsc}`;
    }
    return `alert_${this.fundId}`;
  }

  public onClosePopUp(): void {
    this.modal.close();
  }

  public onConfirmPopUp(): void {
    this.dialogService.setDialogClosedObs(this.modal);
    this.onClosePopUp();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
